import { useCallback, useEffect, useState } from 'react';
import { ApolloError, WatchQueryFetchPolicy } from '@apollo/client';
import {
  FreeBetPromo,
  FreeSpinPromo,
  GigCommonPromo,
  GigPromo,
  MagicXPromo,
  ParsedPromo,
  UserGigQuery,
} from '../../components/Layout/Promotions/types';
import { useUserContext } from '../../providers/UserProvider';
import { UserProfileBonusStatus } from '../../types.__generated__';
import { xTenantId } from '../../consts';
import {
  UserBonusesQuery,
  useUserBonusesLazyQuery,
} from '../../libs/graphql/baseAppAPI/queries/__generated__/user-promo-collection.generated';
import { Endpoint } from '../../models/api.model';
import {
  useTagsObjectLazyQuery,
  TagsObjectQuery,
} from '../../libs/graphql/baseAppAPI/queries/__generated__/tags-get.query.generated';
import {
  UserFreeBetQuery,
  useUserFreeBetLazyQuery,
} from '../../libs/graphql/baseAppAPI/queries/__generated__/user-free-bet-get.generated';
import {
  UserFreeSpinQuery,
  useUserFreeSpinLazyQuery,
} from '../../libs/graphql/baseAppAPI/queries/__generated__/user-free-spin-get.generated';
import { getActiveMxPromotions, getAvailablegMxPromotions } from '../../components/Layout/Promotions/services';
import { PlatformPromos, PromoTagCategory, UserTags } from './types';
import { getMagicXCTA } from '../../components/Layout/Promotions/utils';
import { useAuth } from 'react-oidc-context';

export const useFetchPlatformPromos = (): PlatformPromos => {
  const { userProfileId, user } = useUserContext();
  const { isAuthenticated } = useAuth();

  const [isPromosLoading, setIsPromotionsLoading] = useState<boolean>(false);
  const [fetchMagicXError, setFetchMagicXError] = useState<unknown>('');
  const [userTags, setUserTags] = useState<UserTags>([]);
  const [magicXPromos, setMagicXPromos] = useState<ParsedPromo[]>([]);
  const [depositPromos, setDepositPromos] = useState<ParsedPromo[]>([]);
  const [rewardsPromos, setRewardsPromos] = useState<ParsedPromo[]>([]);

  const onQueryError = (e: ApolloError): void => console.error(e.message);

  const handlePlatformPromos = useCallback((data: UserGigQuery): void => {
    const gigBonuses = (data as UserBonusesQuery).userBonuses?.data;
    const gigFreeBets = (data as UserFreeBetQuery).userFreeBet?.data;
    const gigFreeSpins = (data as UserFreeSpinQuery).userFreeSpin?.data;

    const resGigBonuses = gigBonuses || gigFreeBets || gigFreeSpins || [];

    // First, handle the Gig bonuses merging as before
    const mergedPromotions: ParsedPromo[] = (resGigBonuses as GigCommonPromo[]).map((gigPromo: GigCommonPromo) => {
      const sdGigPromo = gigPromo as GigPromo;
      const freeSpin = gigPromo as FreeSpinPromo;
      const freeBet = gigPromo as FreeBetPromo;
      const magicXPromo = gigPromo as MagicXPromo;
      const sdGigPromoFreeBet = gigPromo as GigPromo | FreeBetPromo;

      const resPromoId = sdGigPromo?.bonusTemplateId || freeSpin?.freeSpinsTemplateId || freeBet?.freeBetTemplateId;

      // It's using as we can't currently rely on bonusTemplateId that's also placed for free bets and spins
      const cancellable = !(freeSpin?.freeSpinsTemplateId || freeBet?.freeBetTemplateId);

      const isFreeBet = !!freeBet?.freeBetTemplateId;

      return {
        id: sdGigPromo?.id || freeSpin?.id || freeBet?.id || magicXPromo?.id,
        bonusId: resPromoId,
        status: (gigPromo as GigPromo)?.status,
        expiresOn: (gigPromo as GigPromo)?.expiresOn || magicXPromo.expiry,
        createdOn: sdGigPromo?.createdOn,
        wageringRequirementAmount: sdGigPromo?.wageringRequirementAmount,
        wageredAmount: sdGigPromo?.wageredAmount,
        balanceAmount: sdGigPromoFreeBet?.balanceAmount,
        convertedAmount: sdGigPromoFreeBet?.convertedAmount,
        cancellable,
        isFreeBet,
      };
    });

    gigBonuses
      ? setDepositPromos(mergedPromotions)
      : setRewardsPromos((prevPromotions) => [...prevPromotions, ...(mergedPromotions as ParsedPromo[])]);
  }, []);

  const handleMagicXPromos = (magicXPromotions: MagicXPromo[], { isActive }: { isActive: boolean }): void => {
    // First, handle the Gig bonuses merging as before
    const mergedMagicXPromotions: ParsedPromo[] = magicXPromotions.map((magicXPromo) => {
      const {
        id: magicId,
        expiry: expiresOn,
        start: createdOn,
        milestones,
        achievementValue: wageredAmount,
        winAmount: balanceAmount,
        type,
        slug: magicXSlug,
      } = magicXPromo;
      const { value: wageringRequirementAmount, level: convertedAmount } = milestones?.[0] || {};
      const magicXCta = getMagicXCTA(magicXPromo);

      const status = isActive ? UserProfileBonusStatus.Claimed : UserProfileBonusStatus.Available;

      return {
        magicId,
        status,
        expiresOn,
        createdOn,
        wageringRequirementAmount,
        wageredAmount,
        balanceAmount,
        convertedAmount,
        magicXCta,
        magicXSlug,
        cancellable: false,
        type,
      };
    });

    setMagicXPromos((prevPromotions) => [...prevPromotions, ...mergedMagicXPromotions]);
  };

  const handleUserTags = (userTagsData: TagsObjectQuery): void => {
    setUserTags(
      userTagsData.tagsObject
        ?.filter((item) => item.tag?.tagCategory?.name === PromoTagCategory.BONUS)
        .map((item) => item.tag?.id)
    );
  };

  const gigFetchData = {
    variables: {
      xTenantId,
      userProfileId,
      pageLimit: 1000,
    },
    fetchPolicy: 'network-only' as WatchQueryFetchPolicy,
    onCompleted: handlePlatformPromos,
    notifyOnNetworkStatusChange: true,
    onError: onQueryError,
    context: { endpoint: Endpoint.appAPI },
  };

  const [fetchBonusesGig, { error: fetchBonusesGigError }] = useUserBonusesLazyQuery(gigFetchData);
  const [fetchBonusesFreeBetGig, { error: fetchBonusesFreeBetGigError }] = useUserFreeBetLazyQuery(gigFetchData);
  const [fetchBonusesFreeSpinGig, { error: fetchBonusesFreeSpinGigError }] = useUserFreeSpinLazyQuery(gigFetchData);

  const [fetchUserTags, { error: fetchUserTagsError }] = useTagsObjectLazyQuery({
    variables: {
      xTenantId,
      externalResource: 'UserProfileId',
      externalResourceId: userProfileId,
    },
    fetchPolicy: 'network-only' as WatchQueryFetchPolicy,
    onCompleted: handleUserTags,
    notifyOnNetworkStatusChange: true,
    onError: onQueryError,
    context: { endpoint: Endpoint.appAPI },
  });

  const promotionsLoadingError =
    fetchBonusesGigError ||
    fetchBonusesFreeBetGigError ||
    fetchBonusesFreeSpinGigError ||
    fetchUserTagsError ||
    fetchMagicXError;

  const fetchMagicXPromotions = async (): Promise<void> => {
    try {
      const activeMXPromotions = await getActiveMxPromotions(user?.email);
      const receivedAvailableMXPromotions = await getAvailablegMxPromotions(user?.email);

      // Filter promotions that may be available and active at the same time
      const availableMXPromotions = receivedAvailableMXPromotions.filter(
        (availablePromo) => !activeMXPromotions.find((activePromo) => activePromo.slug === availablePromo.slug)
      );

      handleMagicXPromos(activeMXPromotions, { isActive: true });
      handleMagicXPromos(availableMXPromotions, { isActive: false });
    } catch (error) {
      setFetchMagicXError(error);
    }
  };

  useEffect(() => {
    (async (): Promise<void> => {
      if (user?.email) {
        await fetchBonusesFreeBetGig();
        await fetchBonusesFreeSpinGig();
        await fetchMagicXPromotions();
        await fetchBonusesGig();

        await fetchUserTags();

        setIsPromotionsLoading(false);
      }
    })();
  }, [user?.email]);

  useEffect(() => {
    isAuthenticated && setIsPromotionsLoading(true);
  }, [isAuthenticated]);

  return {
    platformPromos: {
      magicXPromos,
      depositPromos,
      rewardsPromos,
      userTags,
    },
    isPromosLoading,
    promosLoadingError: promotionsLoadingError,
  };
};
